import type { Practice } from '../types';
import { normalizePracticeLocations } from './practiceLocations';

/** Strip nested Lambda / API Gateway `{ body: ... }` wrappers. */
export function unwrapApiPayload(raw: unknown): unknown {
  let data: unknown = raw;
  for (let i = 0; i < 4; i++) {
    if (data && typeof data === 'object' && data !== null && 'body' in data) {
      const next = (data as { body: unknown }).body;
      if (next === undefined) break;
      data = next;
    } else break;
  }
  return data;
}

/**
 * Ensures `locations` is always a `PracticeLocation[]`:
 * - Parses JSON string if the API stored locations that way
 * - Normalizes legacy string entries
 * - If the server omits `locations`, reuses `fallback` (e.g. the payload you just saved)
 */
export function normalizePracticeFromApi(raw: unknown, fallback?: Partial<Practice>): Practice {
  const unwrapped = unwrapApiPayload(raw);
  const fromServer =
    unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)
      ? (unwrapped as Partial<Practice>)
      : {};

  const merged: Partial<Practice> = { ...fallback, ...fromServer };

  let locRaw: unknown;
  if (fromServer.locations !== undefined && fromServer.locations !== null) {
    locRaw = fromServer.locations;
  } else {
    locRaw = fallback?.locations;
  }

  if (typeof locRaw === 'string') {
    try {
      locRaw = JSON.parse(locRaw);
    } catch {
      locRaw = [];
    }
  }

  const locations = normalizePracticeLocations(locRaw ?? []);

  // Always ensure id is resolved — backends often return only _id (MongoDB ObjectId)
  const resolvedId = (merged.id && merged.id !== 'undefined') ? merged.id : ((merged as any)._id ?? '');

  return {
    ...(merged as Practice),
    id: resolvedId,
    locations,
  };
}

/** GET /practice list — array or wrapped shape. */
export function normalizePracticeListFromApi(raw: unknown): Practice[] {
  const unwrapped = unwrapApiPayload(raw);
  if (Array.isArray(unwrapped)) {
    return unwrapped.map((p) => normalizePracticeFromApi(p));
  }
  if (unwrapped && typeof unwrapped === 'object') {
    const o = unwrapped as Record<string, unknown>;
    if (Array.isArray(o.practices)) return o.practices.map((p) => normalizePracticeFromApi(p));
    if (Array.isArray(o.data)) return o.data.map((p) => normalizePracticeFromApi(p));
    if (Array.isArray(o.items)) return o.items.map((p) => normalizePracticeFromApi(p));
  }
  return [];
}
